import { Injectable } from '@angular/core';
import { CookieOptions } from '../interfaces';
import { CookieService } from './cookie.service';
import { Logger } from '@app/core/logging';
import { environment } from '@env/environment';

/**
 * Represents the value of the "Cache Key" cookie.
 *
 * Value is composed of the region Id, followed by the lang and the currency code,
 * separated by a dot character. eg. `1.fr.EUR`
 */
export interface CkCookie {
  regionId: string;
  languageCode: string;
  currencyCode: string;
}

/**
 * Represents the value of the "Country Code" cookie.
 *
 * Value is an encoded JSON object, example:
 * {
 *   "CC":"GB",
 *   "displayName": {
 *     "fr": "Royaume-Uni",
 *     "en": "United Kingdom",
 *     "de": "Vereinigtes Konigreich",
 *     "us": null,
 *     "it": "Regno Unito",
 *     "es": "Reino Unido"
 *   }
 * }
 */
export interface CcCookie {
  CC: string;
  displayName: { [key: string]: string };
}

/**
 * A service to manage preference cookies.
 *
 * 1. i18n-related cookies
 *
 * These cookies are sent by Akamai when requesting the website for the first time.
 *
 *  - `vc_ck` contains the region Id, followed by the lang and the currency settings.
 *  - `vc_cc` contains the country code associated to hostname.
 */
@Injectable()
export class PreferencesCookieService {
  static readonly CK_COOKIE = 'vc_ck';
  static readonly CC_COOKIE = 'vc_cc';

  cookieOptions: CookieOptions = {
    domain: environment.cookieOptions.domain,
    expires: new Date('2035-01-24T08:17:00+00:00'),
    path: '/'
  };

  constructor(private cookieService: CookieService, private logger: Logger) {}

  /**
   * Returns the "Country Code" cookie.
   * @returns the cookie value
   */
  getCcCookie(): CcCookie {
    const value = this.cookieService.getObject(PreferencesCookieService.CC_COOKIE);
    this.logger.debug(`Read vc_cc cookie: ${JSON.stringify(value)}`);

    return value;
  }

  /**
   * Sets the "Country Code" cookie.
   *
   * @param countryCode ISO country code
   * @param names display names
   */
  setCcCookie(countryCode: string, names: any = {}): void {
    const value: CcCookie = { CC: countryCode, displayName: names };
    this.logger.debug(`Set vc_cc cookie = ${value}`);

    this.cookieService.setObject(PreferencesCookieService.CC_COOKIE, value, this.cookieOptions);
  }

  /**
   * Returns the "Cacke Key" cookie.
   * @returns the cookie value
   */
  getCkCookie(): CkCookie {
    let parsedValue: CkCookie;
    const cookieValue = this.cookieService.get(PreferencesCookieService.CK_COOKIE);

    if (cookieValue !== undefined) {
      parsedValue = PreferencesCookieService.parseCkCookie(cookieValue);
    }
    this.logger.debug(`Read vc_ck cookie: ${cookieValue}`, parsedValue);

    return parsedValue;
  }

  /**
   * Sets the "Cache Key" cookie.
   *
   * @param regionId the region Id
   * @param languageCode 2-letter language code
   * @param currencyCode ISO currency code
   */
  setCkCookie(regionId: string, languageCode: string, currencyCode: string): void {
    const cookieValue = `${regionId}.${languageCode}.${currencyCode}`;
    this.logger.debug(`Set vc_ck cookie = ${cookieValue}`);
    this.cookieService.set(PreferencesCookieService.CK_COOKIE, cookieValue, this.cookieOptions);
  }

  /**
   * Parses the "CK" cookie value and returns it as an object.
   * @param cookieValue Cookie value as a string
   * @returns Parsed value
   */
  static parseCkCookie(cookieValue: string): CkCookie {
    let parsedValue: CkCookie;

    if (cookieValue !== undefined && cookieValue.length > 0) {
      const tokens = cookieValue.split('.');

      if (tokens.length === 3) {
        parsedValue = {
          regionId: tokens[0],
          languageCode: tokens[1],
          currencyCode: tokens[2]
        };
      }
    }

    return parsedValue;
  }
}
